<script setup>
import { ref } from 'vue'

defineProps({
	modelValue: { default: '' },
	label: { type: String, default: '' },
	type: { type: String, default: 'text' },
	id: { type: String, default: '' },
	autocomplete: { type: String, default: 'off' },
	inputmode: { type: String, default: 'text' },
	hasError: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
	// Example value revealed only while the field is focused (label floated).
	hint: { type: String, default: '' },
	// Optional helper/error text shown below the field. Rendered only when set.
	helper: { type: String, default: '' },
	// Which surface the field sits on. '1' (default) keeps the original auth-page
	// look; '2' renders a filled variant that reads cleanly on bg-surface-2 panels
	// (e.g. the side sliders).
	surface: { type: String, default: '1' },
	// Compact variant: shorter height / tighter padding / smaller text. For dense
	// contexts like modals where the full 56px field feels too heavy.
	dense: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'keydown', 'keyup', 'keypress', 'change'])
const inputEl = ref(null)

defineExpose({ focus: () => inputEl.value?.focus() })
</script>

<template>
	<div class="ff" :class="{ 'ff--error': hasError, 'ff--s2': surface === '2', 'ff--dense': dense }">
		<input
			:id="id"
			ref="inputEl"
			class="ff__input"
			:placeholder="hint || ' '"
			:type="type"
			:inputmode="inputmode"
			:autocomplete="autocomplete"
			:disabled="disabled"
			:value="modelValue"
			@input="emit('update:modelValue', $event.target.value)"
			@focus="emit('focus', $event)"
			@blur="emit('blur', $event)"
			@keydown="emit('keydown', $event)"
			@keyup="emit('keyup', $event)"
			@keypress="emit('keypress', $event)"
			@change="emit('change', $event)"
		/>
		<label :for="id" class="ff__label">{{ label }}</label>
		<p v-if="helper" class="ff__helper" :class="{ 'ff__helper--error': hasError }">{{ helper }}</p>
	</div>
</template>

<style scoped>
.ff {
	position: relative;
	width: 100%;
}
.ff__input {
	width: 100%;
	height: 56px;
	padding: 20px 14px 6px;
	border-radius: 12px;
	background-color: var(--castr-field);
	border: 1px solid var(--castr-border);
	color: var(--castr-text);
	font-size: 15px;
	outline: none;
	transition:
		border-color 0.15s ease,
		box-shadow 0.15s ease;
}
.ff__input:hover:not(:focus) {
	border-color: var(--castr-border-strong);
}
.ff__input:focus {
	border-color: var(--castr-blue);
	box-shadow: 0 0 0 3px var(--castr-focus-ring);
}
/* Hide the hint while the label sits inside the field; reveal it on focus
   once the label has floated up. */
.ff__input::placeholder {
	color: transparent;
	transition: color 0.15s ease;
}
.ff__input:focus::placeholder {
	color: var(--castr-text-muted);
}
.ff__label {
	position: absolute;
	left: 15px;
	top: 50%;
	transform: translateY(-50%);
	color: var(--castr-text-muted);
	font-size: 15px;
	pointer-events: none;
	transition:
		top 0.15s ease,
		font-size 0.15s ease,
		color 0.15s ease,
		transform 0.15s ease;
}
/* Float the label up when the field is focused or filled. */
.ff__input:focus ~ .ff__label,
.ff__input:not(:placeholder-shown) ~ .ff__label {
	top: 9px;
	transform: none;
	font-size: 11px;
	font-weight: 500;
	letter-spacing: 0.02em;
	color: var(--castr-text-muted);
}
.ff__input:focus ~ .ff__label {
	color: var(--castr-blue-soft);
}
.ff--error .ff__input {
	border-color: var(--castr-danger);
}
.ff--error .ff__input:focus {
	box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.15);
}
/* Filled variant for fields placed on a bg-surface-2 panel (side sliders).
   Uses surface-3 like the surrounding form controls so it reads cleanly. */
.ff--s2 .ff__input {
	background-color: var(--surface-3, #343e61);
	border-color: var(--surface-4, #404b72);
}
.ff--s2 .ff__input:hover:not(:focus) {
	border-color: var(--surface-5, #4f5c87);
}
/* On the lighter surface-2 panels the blue focused label is low-contrast/hard
   to read. The blue border already signals focus, so use a readable light
   label instead. (Auth pages use surface-1 and keep the blue label.) */
.ff--s2 .ff__input:focus ~ .ff__label {
	color: #b4bcd6;
}
.ff__helper {
	margin: 4px 2px 0;
	font-size: 12px;
	line-height: 1.3;
	color: var(--castr-text-muted);
}
.ff__helper--error {
	color: var(--castr-danger);
}
/* Compact variant — shorter, tighter fields for dense contexts (modals). */
.ff--dense .ff__input {
	height: 44px;
	padding: 15px 12px 3px;
	border-radius: 8px;
	font-size: 14px;
}
.ff--dense .ff__label {
	left: 13px;
	font-size: 14px;
}
.ff--dense .ff__input:focus ~ .ff__label,
.ff--dense .ff__input:not(:placeholder-shown) ~ .ff__label {
	top: 6px;
	font-size: 10px;
}
.ff--dense .ff__helper {
	font-size: 11px;
}
/* iOS Safari zooms focused inputs under 16px and breaks mobile modal layout. */
@media (max-width: 767px) {
	.ff__input,
	.ff--dense .ff__input {
		font-size: 16px;
	}
}
</style>
